import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Text, Paper, Title } from '@mantine/core';
import { ScimAceEditor } from './ScimAceEditor';

export interface JsonViewerProps {
    /** JSON object or array to display (will be stringified). */
    data: any;
    /** Optional heading above the viewer. */
    title?: string;
    /** Optional description below the title. */
    description?: string;
    /** When true, format Body/body values as multiline (e.g. for email templates). Default false. */
    formatBodyMultiline?: boolean;
    /** Editor height. */
    height?: string;
    /** When true, fold nested objects/arrays on initial render and data updates. */
    collapseObjectsByDefault?: boolean;
}

function formatBodyMultilineStringify(data: any, indent: number = 2): string {
    let jsonString = JSON.stringify(data, null, indent);
    const bodyRegex = /("(?:Body|body)"\s*:\s*)"((?:[^"\\]|\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4}))*)"/g;
    return jsonString.replace(bodyRegex, (match, prefix, escapedValue) => {
        let unescapedValue: string;
        try {
            unescapedValue = JSON.parse(`"${escapedValue}"`);
        } catch {
            return match;
        }
        const shouldFormat = unescapedValue.includes('<') ||
            unescapedValue.includes('\n') ||
            unescapedValue.length > 100;
        if (shouldFormat) {
            const lines = unescapedValue.split('\n');
            const nestedIndent = ' '.repeat(indent * 2);
            if (lines.length > 1) {
                const formattedLines: string[] = [];
                lines.forEach((line, index) => {
                    const escapedLine = line
                        .replace(/\\/g, '\\\\')
                        .replace(/"/g, '\\"');
                    if (index === 0) {
                        formattedLines.push(`${prefix}"${escapedLine}`);
                    } else if (index === lines.length - 1) {
                        formattedLines.push(`${nestedIndent}${escapedLine}"`);
                    } else {
                        formattedLines.push(`${nestedIndent}${escapedLine}`);
                    }
                });
                return formattedLines.join('\n');
            }
        }
        return match;
    });
}

/**
 * Read-only JSON viewer with syntax highlighting and code folding (ScimAceEditor).
 * Use formatBodyMultiline when displaying payloads that may contain Body/body (e.g. email templates).
 */
export const JsonViewer: React.FC<JsonViewerProps> = ({
    data,
    title,
    description,
    formatBodyMultiline = false,
    height = '500px',
    collapseObjectsByDefault = false
}) => {
    const editorRef = useRef<any>(null);
    const formattedJson = useMemo(() => {
        if (data === undefined || data === null) {
            return '{}';
        }
        try {
            return formatBodyMultiline
                ? formatBodyMultilineStringify(data, 2)
                : JSON.stringify(data, null, 2);
        } catch (e) {
            return String(data);
        }
    }, [data, formatBodyMultiline]);

    const applyCollapsedView = useCallback((editor: any) => {
        if (!collapseObjectsByDefault || !editor) return;
        window.requestAnimationFrame(() => {
            try {
                const session = editor.getSession?.() ?? editor.session;
                session?.unfold?.();
                session?.foldAll?.(2);
                editor.gotoLine?.(1, 0, true);
                editor.clearSelection?.();
            } catch {
                // Folding is best-effort only.
            }
        });
    }, [collapseObjectsByDefault]);

    useEffect(() => {
        if (editorRef.current) {
            applyCollapsedView(editorRef.current);
        }
    }, [applyCollapsedView, formattedJson]);

    return (
        <Box p="sm">
            {title != null && title !== '' && (
                <Title order={5} mb="xs">
                    {title}
                </Title>
            )}
            {description && (
                <Text size="sm" c="dimmed" mb="sm">
                    {description}
                </Text>
            )}
            <Paper p="sm" withBorder>
                <ScimAceEditor
                    mode="json"
                    value={formattedJson}
                    readOnly={true}
                    name="json-viewer"
                    fontSize={14}
                    showPrintMargin={false}
                    highlightActiveLine={false}
                    height={height}
                    onLoad={(editor) => {
                        editorRef.current = editor;
                        applyCollapsedView(editor);
                    }}
                />
            </Paper>
        </Box>
    );
};
